import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { IAddressValues, ParameterGroup } from '../../types/SetpointManagerTypes';
import { fetchParameterGroups } from '../../services/setpoints/SetpointService';
import type { RootState } from '../../store';

interface ParameterGroupsState {
  groups: ParameterGroup[];
  loading: boolean;
  error: string | null;
  operation: string;
  writeItems: IAddressValues;
  readItems: Record<string, any>;
  // Track selected rows (parameter ids) in Redux for global access
  selectedRows: string[];
}

const initialState: ParameterGroupsState = {
  groups: [],
  loading: false,
  error: null,
  operation: '',
  writeItems: {},
  readItems: {},
  selectedRows: [],
};

export const fetchGroups = createAsyncThunk(
  'parameterGroups/fetchGroups',
  async ({ assetId, showLoading = true }: { assetId: string; showLoading?: boolean }, { rejectWithValue }) => {
    console.log(showLoading);
    try {
      const groups = await fetchParameterGroups(assetId);
      return groups;
    } catch (err) {
      return rejectWithValue('Failed to load parameter groups');
    }
  },
);

// Thunk: apply payload from controller scan to the latest groups from state
export const applySetpointPayload =
  (payload: Record<number, number[]>) => (dispatch: (action: any) => void, getState: () => RootState) => {
    const {
      parameterGroups: { groups },
    } = getState();

    if (!payload || !Array.isArray(payload[1]) || !Array.isArray(payload[3])) return;

    const keys = payload[1];
    const values = payload[3];
    const newVal: Record<string, any> = {};
    Array.from(keys).forEach((item) => (newVal[item] = true));
    dispatch(setReadItems(newVal));

    const updatedGroups: ParameterGroup[] = groups.map((group) => {
      const updatedSetpoints = group.setpoints.map((param) => {
        const paramKey = Number(param.parameter);
        const idx = keys.indexOf(paramKey);
        if (idx !== -1) {
          return { ...param, currentValue: values[idx] };
        }
        return param;
      });
      return { ...group, setpoints: updatedSetpoints };
    });

    dispatch(setGroups(updatedGroups));
  };

const parameterGroupsSlice = createSlice({
  name: 'parameterGroups',
  initialState,
  reducers: {
    setGroups(state, action: PayloadAction<ParameterGroup[]>) {
      state.groups = action.payload;
    },
    applyBackupValues(state, action: PayloadAction<IAddressValues>) {
      const updates = action.payload;
      state.groups = state.groups.map((group) => ({
        ...group,
        setpoints: group.setpoints.map((sp) =>
          Object.prototype.hasOwnProperty.call(updates, sp.parameter)
            ? { ...sp, backupValue: String(updates[sp.parameter]) }
            : sp,
        ),
      }));
    },
    updateGroup(state, action: PayloadAction<ParameterGroup>) {
      const idx = state.groups.findIndex((g) => g.setpointGroup === action.payload.setpointGroup);
      if (idx !== -1) state.groups[idx] = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setOperation(state, action: PayloadAction<string>) {
      state.operation = action.payload;
    },
    setReadItems(state, action: PayloadAction<Record<string, any>>) {
      state.readItems = action.payload;
    },
    addWriteItem(state, action: PayloadAction<IAddressValues>) {
      state.writeItems = { ...state.writeItems, ...action.payload };
    },
    removeWriteItem(state, action: PayloadAction<string>) {
      const updates = state.writeItems;
      delete updates[action.payload];
      state.writeItems = { ...updates };
    },
    resetReadWriteItems(state) {
      state.writeItems = {};
      state.readItems = {};
    },
    // Selection management
    setSelectedRows(state, action: PayloadAction<string[]>) {
      state.selectedRows = action.payload;
    },
    clearSelectedRows(state) {
      state.selectedRows = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state, action) => {
        state.loading = action.meta.arg.showLoading !== false;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.groups = action.payload;
        state.loading = false;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setGroups,
  applyBackupValues,
  updateGroup,
  setError,
  setOperation,
  setReadItems,
  addWriteItem,
  resetReadWriteItems,
  setSelectedRows,
  clearSelectedRows,
  removeWriteItem,
} = parameterGroupsSlice.actions;
export default parameterGroupsSlice.reducer;
export { ParameterGroupsState };
